import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, KeyRound } from 'lucide-react';
import AuthLayout from '../layouts/AuthLayout';

export default function ResetPwd() {
  const [formData, setFormData] = useState({
    email: ''
  });
  const [errors, setErrors] = useState({
    email: ''
  });
  const [generalError, setGeneralError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {
      email: ''
    };
    let isValid = true;

    // Email validation
    if (!formData.email) {
      newErrors.email = 'This field is required.';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email address.';
      isValid = false;
    }

    setErrors(newErrors);

    // Focus on the field with an error
    if (!isValid) {
      document.getElementById('email').focus();
    }

    return isValid;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE}/signup/forgotpassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8'
        },
        body: JSON.stringify({
          Email: formData.email
        })
      });

      if (response.ok) {
        setGeneralError('If this email exists in our system, you will receive a password reset link shortly.');
        setTimeout(() => {
          navigate('/login');
        }, 8000);
      } else {
        setGeneralError('Something went wrong. Please try again later.');
      }
    } catch (err) {
      setGeneralError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="max-w-sm w-full sm:max-w-md p-4 my-8 bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] space-y-2">
        <div className="flex flex-col items-center mb-6">
          <KeyRound className="w-8 h-8 text-[var(--accent-primary)] mb-4" strokeWidth={1} />
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">Reset Password</h2>
        </div>
        
        <form onSubmit={handleSubmit} noValidate autoComplete="on" className="space-y-4">
          <div>
            <div className="relative">
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                className={`w-full p-2 pl-10 border rounded bg-[var(--card-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] disabled:opacity-50 ${
                  errors.email ? 'border-red-500' : 'border-[var(--border-color)]'
                }`}
                placeholder="Enter your email"
              />
              <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {errors.email && (
              <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
            )}
          </div>

          {generalError && (
            <div className="p-4 rounded bg-[var(--hover-light)] shadow-sm">
              <div className="flex flex-col items-center gap-4">
                <div className="flex justify-center mb-4">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM19.6 8.25L12.53 12.67C12.21 12.87 11.79 12.87 11.47 12.67L4.4 8.25C4.15 8.09 4 7.82 4 7.53C4 6.86 4.73 6.46 5.3 6.81L12 11L18.7 6.81C19.27 6.46 20 6.86 20 7.53C20 7.82 19.85 8.09 19.6 8.25Z" fill="var(--accent-secondary)"/>
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-[var(--text-primary)] font-medium mb-1">Check your email inbox</p>
                  <p className="text-[var(--text-secondary)] text-sm">{generalError}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] active:bg-[var(--accent-primary)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent-primary)]"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                  Sending...
                </>
              ) : (
                'Reset Password'
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-fit flex items-center px-4 py-2 font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Return
            </button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
} 